import React, { useState, useEffect } from "react";
import { Wallet, ArrowUpRight, ArrowDownRight, Plus, Users, ShieldCheck, X, FileText, Calendar, Paperclip, Eye } from "lucide-react";
import api from "../api/axiosInstance";

const StaffWalletRegistry = () => {
    const [supervisors, setSupervisors] = useState([]);
    const [statement, setStatement] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [balance, setBalance] = useState(0);
    const [loading, setLoading] = useState(true);

    // Handover Allocation Form State
    const [modalOpen, setModalOpen] = useState(false);
    const [amount, setAmount] = useState("");
    const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
    const [remarks, setRemarks] = useState("");
    const [attachment, setAttachment] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchRegistryData();
    }, []);

    const fetchRegistryData = async (targetUserId = null) => {
        setLoading(true);
        try {
            const url = targetUserId ? `get_wallet_intelligence.php?user_id=${targetUserId}` : "get_wallet_intelligence.php";
            const res = await api.get(url);

            if (res.data.supervisors && res.data.supervisors.length > 0) {
                setSupervisors(res.data.supervisors);
                if (!targetUserId) {
                    setSelectedUser(res.data.supervisors[0]);
                    setBalance(res.data.supervisors[0].balance);
                }
            }
            setStatement(res.data.statement || []);
            if (targetUserId) {
                setBalance(res.data.current_balance);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectUser = (user) => {
        setSelectedUser(user);
        fetchRegistryData(user.id);
    };

    const handleFileChange = (e) => {
        if (e.target.files.length > 0) {
            setAttachment(e.target.files[0]);
        }
    };

    const handleIssueCapital = async (e) => {
        e.preventDefault();
        if (!amount || parseFloat(amount) <= 0) return alert("Enter valid disbursement amount");

        setSubmitting(true);
        const formData = new FormData();
        formData.append("user_id", selectedUser.id);
        formData.append("amount", parseFloat(amount));
        formData.append("transaction_date", date);
        formData.append("remarks", remarks);
        if (attachment) {
            formData.append("attachment", attachment);
        }

        try {
            const res = await api.post("allocate_wallet_cash.php", formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            if (res.data.status === "success") {
                setModalOpen(false);
                setAmount("");
                setDate(new Date().toISOString().split("T")[0]);
                setRemarks("");
                setAttachment(null);
                fetchRegistryData(selectedUser.id);
            }
        } catch (err) {
            alert(err.response?.data?.message || "Allocation failed");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading && supervisors.length === 0) return <div className="p-20 text-center font-black animate-pulse text-slate-400">SYNCING CAPITAL METRICS...</div>;

    return (
        <div className="p-8 space-y-8 bg-slate-50/50 min-h-screen">
            {/* Title Header */}
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-4xl font-black italic tracking-tighter uppercase text-slate-900">Ecosystem Cash Matrix</h1>
                    <p className="text-[10px] font-black uppercase text-indigo-500 tracking-widest mt-1">Supervisor Wallet Monitoring & Statement Reconciliation</p>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl border border-slate-100 text-[10px] font-black uppercase text-emerald-600 shadow-sm">
                    <ShieldCheck size={14} /> Master Owner Node
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Panel: Account Selector List */}
                <div className="bg-white p-6 rounded-[36px] border border-slate-100 shadow-sm space-y-4 h-fit">
                    <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-2 flex items-center gap-2">
                        <Users size={14} /> Staff Accounts Vault
                    </h3>
                    <div className="space-y-2">
                        {supervisors.map((sup) => (
                            <button
                                key={sup.id}
                                onClick={() => handleSelectUser(sup)}
                                className={`w-full flex justify-between items-center p-5 rounded-2xl border transition-all text-left ${selectedUser?.id === sup.id ? "bg-slate-900 border-slate-900 text-white shadow-md" : "bg-slate-50 border-transparent hover:bg-slate-100"
                                    }`}
                            >
                                <div>
                                    <p className="font-black text-sm uppercase italic">{sup.name}</p>
                                    <p className={`text-[9px] font-bold uppercase tracking-wider ${selectedUser?.id === sup.id ? "text-indigo-300" : "text-indigo-500"}`}>{sup.role}</p>
                                </div>
                                <span className={`font-black text-sm ${selectedUser?.id === sup.id ? "text-emerald-400" : "text-slate-800"}`}>
                                    ₹{parseFloat(sup.balance).toLocaleString()}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Right Panels: Card Monitor & Reconciled Statement Table */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-slate-950 text-white p-8 rounded-[40px] shadow-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-6 relative overflow-hidden">
                        <div className="space-y-1">
                            <span className="text-[9px] font-black text-indigo-400 uppercase tracking-widest block">Active Liquidity Floating Pool</span>
                            <h2 className="text-sm font-black uppercase italic text-slate-300">{selectedUser?.name}'s Balance Book</h2>
                            <h1 className="text-4xl font-black italic mt-2 text-white tracking-tight">₹{balance.toLocaleString()}</h1>
                        </div>
                        <button
                            onClick={() => setModalOpen(true)}
                            className="bg-white text-slate-950 px-6 py-3.5 rounded-2xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-indigo-50 hover:scale-105 transition-all"
                        >
                            <Plus size={16} /> Handover Capital Funds
                        </button>
                    </div>

                    {/* Statement Aggregate Table */}
                    <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm p-8 space-y-6">
                        <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                            <FileText size={16} /> Reconciled Statement Account Logs
                        </h3>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-slate-50 text-[9px] font-black uppercase text-slate-400 tracking-wider border-b border-slate-100">
                                    <tr>
                                        <th className="p-4">Type</th>
                                        <th className="p-4">Transaction Source Details</th>
                                        <th className="p-4">Context Date</th>
                                        <th className="p-4 text-center">Attachment</th>
                                        <th className="p-4 text-right">Transaction Volume</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50 text-xs font-bold text-slate-700">
                                    {statement.map((log) => (
                                        <tr key={log.id} className="hover:bg-slate-50/50 transition-all">
                                            <td className="p-4">
                                                <span className={`p-2 rounded-xl inline-block ${log.transaction_type === 'CREDIT' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
                                                    }`}>
                                                    {log.transaction_type === 'CREDIT' ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                <p className="font-black text-slate-900 uppercase text-[13px]">{log.source_type.replace(/_/g, ' ')}</p>
                                                <p className="text-[10px] text-slate-400 font-medium uppercase mt-0.5">{log.remarks}</p>
                                            </td>
                                            <td className="p-4 uppercase text-slate-500 font-black text-[11px]">
                                                {log.transaction_date || log.created_at.split(' ')[0]}
                                            </td>
                                            <td className="p-4 text-center">
                                                {log.attachment_path ? (
                                                    <a
                                                        href={`/${log.attachment_path}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="inline-flex p-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-900 hover:text-white transition-all mx-auto"
                                                    >
                                                        <Eye size={14} />
                                                    </a>
                                                ) : (
                                                    <span className="text-slate-300 font-medium text-[10px] uppercase">None</span>
                                                )}
                                            </td>
                                            <td className={`p-4 text-right font-black text-sm ${log.transaction_type === 'CREDIT' ? 'text-emerald-600' : 'text-slate-800'}`}>
                                                {log.transaction_type === 'CREDIT' ? '+' : '-'} ₹{parseFloat(log.amount).toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                    {statement.length === 0 && (
                                        <tr>
                                            <td colSpan="5" className="text-center p-10 text-[10px] font-black text-slate-400 uppercase tracking-widest">No structural audit trails logged for this account</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- OWNER DETAILED HANDOVER MODAL OVERLAY --- */}
            {modalOpen && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-[40px] shadow-2xl border border-slate-100 w-full max-w-md p-8 animate-in zoom-in-95 duration-150">
                        <div className="flex justify-between items-center pb-4 border-b border-slate-50">
                            <div>
                                <h3 className="text-xl font-black italic uppercase tracking-tighter text-slate-900">Disburse Liquid Capital</h3>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mt-0.5">Recipient target: {selectedUser?.name}</p>
                            </div>
                            <button onClick={() => setModalOpen(false)} className="p-2 text-slate-400 hover:bg-slate-100 rounded-full transition-all"><X size={18} /></button>
                        </div>
                        <form onSubmit={handleIssueCapital} className="space-y-4 mt-6">
                            <div>
                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1.5 ml-1">Quantum Amount to Handover (₹)</label>
                                <input
                                    type="number"
                                    required
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-sm font-black outline-none focus:ring-4 ring-indigo-500/5"
                                    placeholder="e.g. 35000"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1.5 ml-1">Handover Settlement Date</label>
                                <input
                                    type="date"
                                    required
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-sm font-black outline-none"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1.5 ml-1">Reference Voucher / Attachment Receipt</label>
                                <div className="relative flex items-center bg-slate-50 border border-slate-100 rounded-2xl p-4 cursor-pointer hover:bg-slate-100/50 transition-all">
                                    <Paperclip size={16} className="text-slate-400 mr-2" />
                                    <span className="text-xs font-bold text-slate-600 truncate">
                                        {attachment ? attachment.name : "Select Document / Receipt Image"}
                                    </span>
                                    <input
                                        type="file"
                                        accept="image/*,application/pdf"
                                        className="absolute inset-0 opacity-0 cursor-pointer"
                                        onChange={handleFileChange}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1.5 ml-1">Internal Reference Audit Remarks</label>
                                <input
                                    type="text"
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-sm font-bold outline-none"
                                    placeholder="e.g. Direct cash handover for emergency raw materials"
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full bg-indigo-600 disabled:bg-slate-300 text-white font-black text-xs uppercase tracking-widest py-4 rounded-2xl shadow-lg hover:bg-indigo-700 transition-all pt-4"
                            >
                                {submitting ? "Processing Disbursal..." : "Authorize Capital Deployment"}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StaffWalletRegistry;